import DOMPurify, { type Config } from "dompurify";

/**
 * HTML sanitization helpers for rendered rich text — the client-side
 * counterpart of `api/src/common/helpers/sanitizeHtml.ts`.
 *
 * All rich-text content rendered through these helpers (comments, event
 * descriptions, chat notifications, memberzine articles, topic elements) is
 * user-generated. The API sanitizes it on write, but the client re-sanitizes
 * on render as defense in depth (legacy rows, missed write paths).
 *
 * DOMPurify removes scripts, event handlers and `javascript:` URLs; it keeps
 * `data-*` attributes (needed for the TipTap mention markup that
 * `getMentionsFromComment` parses) and styling attributes.
 */

const domPurifyConfig: Config = {
  ADD_ATTR: ["target"],
};

/**
 * Trusted iframe embed hosts for curated content (memberzine articles, topic
 * embedded elements).
 *
 * Keep in sync with `EMBEDDED_IFRAME_HOSTNAMES` in
 * `api/src/common/helpers/sanitizeHtml.ts` — the API restricts stored iframe
 * sources to the same hosts.
 */
const TRUSTED_EMBED_HOSTNAMES = new Set([
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "player.vimeo.com",
  "vimeo.com",
  "loom.com",
  "www.loom.com",
]);

export const isTrustedEmbedUrl = (src: string | null | undefined): boolean => {
  if (!src) {
    return false;
  }
  try {
    const { protocol, hostname, username } = new URL(src);
    return !username && protocol === "https:" && TRUSTED_EMBED_HOSTNAMES.has(hostname);
  } catch {
    return false;
  }
};

/**
 * DOMPurify cannot restrict iframe sources through its config, so a global
 * hook removes every iframe whose `src` does not point at a trusted embed
 * host. For strict sanitization (without `ADD_TAGS: ["iframe"]`) this is a
 * no-op — iframes are removed by DOMPurify's default allowlist anyway.
 */
DOMPurify.addHook("uponSanitizeElement", (node, { tagName }) => {
  if (tagName === "iframe" && !isTrustedEmbedUrl((node as Element).getAttribute("src"))) {
    (node as Element).remove();
  }
});

const domPurifyEmbeddedConfig: Config = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder"],
};

export const dangerouslySetInnerHTML = (content: string | null | undefined) => {
  if (content) {
    return {
      __html: DOMPurify.sanitize(content, domPurifyConfig),
    };
  }
  return undefined;
};

/**
 * Sanitized render helper for curated content that may contain trusted
 * iframe embeds (memberzine articles, topic embedded elements). Behaves like
 * `dangerouslySetInnerHTML` otherwise; iframes from untrusted hosts are
 * removed (see `TRUSTED_EMBED_HOSTNAMES`).
 */
export const dangerouslySetEmbeddedInnerHTML = (content: string | null | undefined) => {
  if (content) {
    return {
      __html: DOMPurify.sanitize(content, domPurifyEmbeddedConfig),
    };
  }
  return undefined;
};
